// ./src/components/features/CreateEventWizard/useEventWizardDraft.ts
//
// Persists the wizard's in-progress draft to localStorage (via AppCache, scoped to
// the signed-in user) so an accidental reload — or the browser silently reloading
// a backgrounded tab — never loses the user's work. On mount we detect a saved
// draft and expose it so the shell can offer "Restore" vs "Start over".

import { useCallback, useRef, useState } from 'react';

import { AppCache } from '@/utils';
import { useApp } from '@/contexts';

import { EventWizardDraft, emptyEventWizardDraft, WizardStepKey } from './wizardTypes';

const DRAFT_CACHE_KEY = 'event-wizard-draft';
// Keep a draft around for a week — long enough to survive "I'll finish tomorrow".
const DRAFT_MAX_AGE = 7 * 24 * 60 * 60 * 1000;

/** What we persist: the draft plus where the user was in the flow. */
interface PersistedWizardState {
  draft: EventWizardDraft;
  stepKey: WizardStepKey;
  savedAt: number;
}

export interface UseEventWizardDraftReturn {
  /** A saved draft found on mount (null if none) — drives the restore prompt. */
  savedState: PersistedWizardState | null;
  /** Whether a restorable draft was found on mount. */
  hasSavedDraft: boolean;
  /** Write the current state to storage. */
  save: (draft: EventWizardDraft, stepKey: WizardStepKey) => void;
  /** Drop any persisted draft (on finish or explicit "start over"). */
  clear: () => void;
}

export const useEventWizardDraft = (): UseEventWizardDraftReturn => {
  const { user } = useApp();
  const userId = user?.id ?? null;

  // Read once on mount — a stable snapshot for the restore decision.
  const savedStateRef = useRef<PersistedWizardState | null | undefined>(undefined);
  if (savedStateRef.current === undefined) {
    const result = AppCache.get<PersistedWizardState>(DRAFT_CACHE_KEY, {
      maxAge: DRAFT_MAX_AGE,
      userId,
    });
    // Guard against a shape that predates the current draft fields.
    const data = result?.data;
    savedStateRef.current =
      data && data.draft && typeof data.stepKey === 'string'
        ? { ...data, draft: { ...emptyEventWizardDraft(), ...data.draft } }
        : null;
  }

  const [savedState] = useState<PersistedWizardState | null>(savedStateRef.current);

  const save = useCallback(
    (draft: EventWizardDraft, stepKey: WizardStepKey) => {
      AppCache.set<PersistedWizardState>(DRAFT_CACHE_KEY, { draft, stepKey, savedAt: Date.now() }, { userId });
    },
    [userId],
  );

  const clear = useCallback(() => {
    AppCache.remove(DRAFT_CACHE_KEY);
  }, []);

  return {
    savedState,
    hasSavedDraft: savedState !== null,
    save,
    clear,
  };
};
